import { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { NotificationSetup } from '../components/NotificationSetup';
import { api } from '../lib/api';
import { getPlatformInfo } from '../lib/platform';
import { useNavigate } from 'react-router-dom';
import { AddPersonIcon, MoonIcon, SunIcon } from '../components/Icons';

export function SettingsPage({
  theme,
  onThemeChange,
}: {
  theme: 'light' | 'dark';
  onThemeChange: (theme: 'light' | 'dark') => void;
}) {
  const { user, signOut } = useAuth();
  const [config, setConfig] = useState<{ nextcloudUrl: string; photosPath: string } | null>(
    null,
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [groupsReady, setGroupsReady] = useState<boolean | null>(null);
  const [settingUpGroups, setSettingUpGroups] = useState(false);
  const [typingPreference, setTypingPreference] = useState<{
    supported: boolean;
    enabled: boolean;
  } | null>(null);
  const [typingPreferenceSaving, setTypingPreferenceSaving] = useState(false);
  const [typingPreferenceError, setTypingPreferenceError] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [updatingApp, setUpdatingApp] = useState(false);
  const navigate = useNavigate();
  const installedVersion = import.meta.env.VITE_APP_VERSION || 'development';
  const updateAvailable = Boolean(latestVersion && latestVersion !== installedVersion);

  const platform = getPlatformInfo();

  useEffect(() => {
    void api
      .config()
      .then((cfg) => setConfig({ nextcloudUrl: cfg.nextcloudUrl, photosPath: cfg.photosPath }))
      .catch(() => undefined);

    // Only admins can invite, so the entry is hidden entirely for everyone
    // else rather than shown and then refused.
    void api
      .adminStatus()
      .then((status) => setIsAdmin(status.isAdmin))
      .catch(() => setIsAdmin(false));

    void api
      .chatSettings()
      .then((preference) => setTypingPreference(preference))
      .catch(() => setTypingPreferenceError('Could not load this chat privacy setting.'));

    void api.version().then(({ version }) => setLatestVersion(version)).catch(() => undefined);
  }, []);

  const updateApp = async () => {
    if (updatingApp) return;
    setUpdatingApp(true);
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
          await registration.update();
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
          window.setTimeout(() => window.location.reload(), 1500);
          return;
        }
      }
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  const toggleTypingPreference = async () => {
    if (!typingPreference?.supported || typingPreferenceSaving) return;
    const enabled = !typingPreference.enabled;
    setTypingPreferenceSaving(true);
    setTypingPreferenceError(null);
    try {
      setTypingPreference(await api.setChatTypingPreference(enabled));
    } catch {
      setTypingPreferenceError('Could not save this setting. Please try again.');
    } finally {
      setTypingPreferenceSaving(false);
    }
  };

  return (
    <>
      <header className="app-header">
        <h1>Settings</h1>
      </header>

      <div className="page">
        {/* The whole card is the way into the profile editor: on a phone a
            small "Edit" link is easy to miss. */}
        <button
          type="button"
          className="card profile-summary"
          onClick={() => navigate('/profile')}
        >
          <div className="profile-summary-avatar">
            {user && (
              <img
                src={api.avatarUrl(user.id, 128, user.avatarVersion)}
                alt=""
                onError={(event) => { event.currentTarget.style.display = 'none'; }}
              />
            )}
            <span aria-hidden="true">{(user?.displayName ?? '?').slice(0, 1).toUpperCase()}</span>
          </div>
          <div className="profile-summary-copy">
            <h2>{user?.displayName}</h2>
            <p>Edit your name, photo and details</p>
          </div>
          <span className="profile-summary-chevron" aria-hidden="true">›</span>
        </button>

        <div className="card">
          <h2>Signed in</h2>
          <p style={{ fontSize: '0.85rem' }}>
            Photos are stored in your family cloud
            {config ? ` under “${config.photosPath}”` : ''}. Everything you share here shows up
            there too.
          </p>
        </div>

        <NotificationSetup />

        <div className="card theme-card">
          <div>
            <h2>Appearance</h2>
            <p>Choose the view that is easiest on your eyes.</p>
          </div>
          <div className="theme-toggle" role="group" aria-label="Color theme">
            <button
              type="button"
              className={theme === 'light' ? 'active' : ''}
              onClick={() => onThemeChange('light')}
              aria-pressed={theme === 'light'}
            >
              <SunIcon /> Light
            </button>
            <button
              type="button"
              className={theme === 'dark' ? 'active' : ''}
              onClick={() => onThemeChange('dark')}
              aria-pressed={theme === 'dark'}
            >
              <MoonIcon /> Dark
            </button>
          </div>
        </div>

        <button
          type="button"
          className="card settings-circle-link"
          onClick={() => navigate('/circles')}
        >
          <span className="settings-circle-icon" aria-hidden="true"><AddPersonIcon /></span>
          <span className="settings-circle-copy">
            <strong>Manage sharing circles</strong>
            <small>See who belongs and add family you already know.</small>
          </span>
          <span className="settings-circle-chevron" aria-hidden="true">›</span>
        </button>

        {isAdmin && (
          <div className="card">
            <h2>Circle setup</h2>
            <p>Check the Nextcloud folders and shares behind Immediate family, Hyte family, and Swainston family.</p>
            {groupsReady === false && <p style={{ color: 'var(--danger)' }}>The setup could not be completed. Check the family cloud connection and try again.</p>}
            {groupsReady === true && <p style={{ color: 'var(--lush-deep)' }}>The three sharing circles are ready.</p>}
            <button
              type="button"
              className="btn btn-primary btn-block"
              style={{ marginTop: 10 }}
              disabled={settingUpGroups}
              onClick={() => {
                setSettingUpGroups(true);
                void api.setupGroups().then(() => setGroupsReady(true)).catch(() => setGroupsReady(false)).finally(() => setSettingUpGroups(false));
              }}
            >
              {settingUpGroups ? 'Setting up…' : groupsReady ? 'Check sharing circles' : 'Set up sharing circles'}
            </button>
          </div>
        )}

        {isAdmin && (
          <div className="card">
            <h2>Invite family</h2>
            <p>
              Add someone to the family. They get an email with a link to choose a password,
              and the photos are shared with them straight away.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              style={{ marginTop: 10 }}
              onClick={() => navigate('/invite')}
            >
              Invite someone
            </button>
          </div>
        )}

        <div className="card chat-settings-card">
          <h2>Chat</h2>
          <p>
            Chat uses the same account as your photos, so there is nothing extra to sign in
            to. Open the Chat tab to see your conversations.
          </p>
          <div className="settings-toggle-row">
            <div className="settings-toggle-copy">
              <strong>Typing indicators</strong>
              <span>
                {typingPreference?.supported === false
                  ? 'This version of Nextcloud Talk does not support this privacy setting.'
                  : 'Let family see when you’re writing. You’ll see theirs too.'}
              </span>
            </div>
            <button
              type="button"
              className="settings-switch"
              role="switch"
              aria-checked={typingPreference?.enabled ?? false}
              aria-label="Share typing indicators"
              aria-busy={typingPreferenceSaving}
              disabled={!typingPreference?.supported || typingPreferenceSaving}
              onClick={() => void toggleTypingPreference()}
            >
              <span aria-hidden="true" />
              <span className="sr-only">
                {typingPreferenceSaving
                  ? 'Saving'
                  : typingPreference?.enabled
                    ? 'On'
                    : 'Off'}
              </span>
            </button>
          </div>
          {typingPreference === null && !typingPreferenceError && (
            <p className="settings-inline-status" role="status">Loading chat privacy…</p>
          )}
          {typingPreferenceSaving && (
            <p className="settings-inline-status" role="status">Saving…</p>
          )}
          {typingPreferenceError && (
            <p className="settings-inline-error" role="alert">{typingPreferenceError}</p>
          )}
        </div>

        {/*
          A plain-language capability readout. When something is not working on
          one person's phone, this is what they can read out over the phone
          instead of anyone having to guess.
        */}
        <div className="card">
          <h2>This device</h2>
          <div style={{ fontSize: '0.87rem', color: 'var(--text-muted)' }}>
            <Row label="Installed to Home Screen" value={platform.isStandalone ? 'Yes' : 'No'} />
            <Row
              label="Notification permission"
              value={
                platform.permission === 'granted'
                  ? 'Allowed'
                  : platform.permission === 'denied'
                    ? 'Blocked'
                    : platform.permission === 'default'
                      ? 'Not asked yet'
                      : 'Not supported'
              }
            />
            <Row
              label="Push supported"
              value={platform.supportsPush && platform.supportsServiceWorker ? 'Yes' : 'No'}
            />
            {platform.isIOS && (
              <Row
                label="iOS version"
                value={platform.iosVersion ? platform.iosVersion.toFixed(2) : 'Unknown'}
              />
            )}
          </div>
        </div>

        <div className="card">
          <h2>Links</h2>
          {config && (
            <p>
              <a href={config.nextcloudUrl} target="_blank" rel="noopener noreferrer">
                Open the family cloud
              </a>
            </p>
          )}
          <p style={{ fontSize: '0.82rem', color: 'var(--text-faint)' }}>
            The full photo library, with search and timeline, lives in Memories there.
          </p>
        </div>

        <div className="card app-version-card">
          <div>
            <h2>App version</h2>
            <p>Version {installedVersion}</p>
          </div>
          {updateAvailable ? (
            <button type="button" className="btn btn-primary" disabled={updatingApp} onClick={() => void updateApp()}>
              {updatingApp ? 'Updating…' : `Update to ${latestVersion}`}
            </button>
          ) : (
            <span className="app-version-current">{latestVersion ? 'Up to date' : 'Checking…'}</span>
          )}
        </div>

        <button
          type="button"
          className="btn btn-block"
          onClick={() => {
            // Clear both the completion flag and any snooze, so the guided
            // flow runs from the top on the next load.
            localStorage.removeItem('family.onboarding.completed');
            localStorage.removeItem('family.onboarding.snoozedUntil');
            window.location.reload();
          }}
          style={{ marginBottom: 10 }}
        >
          Run setup again
        </button>

        <button
          type="button"
          className="btn btn-danger btn-block"
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </button>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '6px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 550 }}>{value}</span>
    </div>
  );
}
